import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Radio,
  FormControlLabel,
  Tooltip,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import {
  doc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";

/* ------------------------------------------------------------------ */
/*  Helper: one-line preview                                          */
/* ------------------------------------------------------------------ */
const preview = (v) => {
  if (v == null) return "—";
  if (Array.isArray(v)) {
    if (!v.length) return "[0 items]";
    if (typeof v[0] === "object") {
      const [k, val] = Object.entries(v[0])[0] || [];
      return `[${v.length}] { ${k}:${String(val).slice(0, 10)} … }`;
    }
    const items = v.slice(0, 3).join(", ");
    return `[${v.length}] ${items}${v.length > 3 ? ", …" : ""}`;
  }
  if (typeof v === "object") {
    const entries = Object.entries(v).slice(0, 2);
    const short = entries
      .map(([k, val]) => `${k}:${String(val).slice(0, 10)}`)
      .join(", ");
    return `{ ${short}${Object.keys(v).length > 2 ? " …" : ""} }`;
  }
  return String(v);
};

/* ------------------------------------------------------------------ */
/*  MergeMembersDialog                                                */
/* ------------------------------------------------------------------ */
export default function MergeMembersDialog({
  open,
  onClose,
  left,
  right,
  afterMerge,
}) {
  // keep track of which doc we intend to KEEP vs REMOVE
  const [kept, setKept] = useState(left || {});
  const [removed, setRemoved] = useState(right || {});

  // whenever the dialog opens (and left/right are non-null), sync local state
  useEffect(() => {
    if (open && left && right) {
      setKept(left);
      setRemoved(right);
    }
  }, [open, left, right]);

  // gather all top-level keys (except "id")
  const allKeys = useMemo(() => {
    if (!open || !kept.id || !removed.id) return [];
    return [
      ...new Set([...Object.keys(kept), ...Object.keys(removed)]),
    ].filter((k) => k !== "id");
  }, [open, kept, removed]);

  // per-field choice: "left" | "right" | "merge"
  const [choice, setChoice] = useState({});
  useEffect(() => {
    if (open) {
      const init = allKeys.map((k) => [k, "left"]);
      setChoice(Object.fromEntries(init));
    }
  }, [open, allKeys]);

  const pick = (field, side) =>
    setChoice((prev) => ({ ...prev, [field]: side }));

  // the button label changes if either side still has pennants
  const keptPennCount = Array.isArray(kept.pennants) ? kept.pennants.length : 0;
  const remPennCount = Array.isArray(removed.pennants)
    ? removed.pennants.length
    : 0;
  const showWarn = keptPennCount > 0 || remPennCount > 0;

  /* ---------------------------------------------------------------- */
  /*  Merge → update Firestore → sweep all references → cleanup       */
  /* ---------------------------------------------------------------- */
  const handleMerge = async () => {
    // 1️⃣ Compose the merged document
    const merged = { ...kept };

    // always merge the pennants arrays
    merged.pennants = Array.from(
      new Set([
        ...(Array.isArray(kept.pennants) ? kept.pennants : []),
        ...(Array.isArray(removed.pennants) ? removed.pennants : []),
      ])
    );

    // then apply per-field choice for everything else
    allKeys.forEach((key) => {
      if (key === "pennants") return; // already merged
      const how = choice[key];
      if (how === "left") return;
      if (how === "merge") {
        const lv = kept[key];
        const rv = removed[key];
        if (Array.isArray(lv) && Array.isArray(rv)) {
          merged[key] = Array.from(new Set([...lv, ...rv]));
        } else if (
          lv &&
          typeof lv === "object" &&
          rv &&
          typeof rv === "object"
        ) {
          merged[key] = { ...lv, ...rv };
        } else {
          merged[key] = rv;
        }
      } else {
        // how === "right"
        merged[key] = removed[key];
      }
    });

    try {
      // 2️⃣ Write merged back to the kept doc
      await updateDoc(doc(db, "members", kept.id), merged);

      // 3️⃣ For every Pennant, replace removed.id → kept.id in its members map
      const pennSnap = await getDocs(collection(db, "Pennants"));
      await Promise.all(
        pennSnap.docs.map(async (pDoc) => {
          const data = pDoc.data() || {};
          const membersMap = data.members || {};
          let changed = false;
          const updateMap = {};

          Object.entries(membersMap).forEach(([sect, arr]) => {
            if (Array.isArray(arr) && arr.includes(removed.id)) {
              updateMap[sect] = arr.map((id) =>
                id === removed.id ? kept.id : id
              );
              changed = true;
            }
          });

          if (changed) {
            await updateDoc(doc(db, "Pennants", pDoc.id), {
              members: { ...membersMap, ...updateMap },
            });
          }

          // 4️⃣ Sweep each "draws" sub-collection
          const drawsCol = collection(db, "Pennants", pDoc.id, "draws");
          const drawSnap = await getDocs(drawsCol);
          await Promise.all(
            drawSnap.docs.map(async (dDoc) => {
              const d = dDoc.data() || {};
              let anyChanged = false;
              const newWeeks = (d.weeks || []).map((wk) => {
                const newMatches = (wk.matches || []).map((m) => {
                  const t1 = m.team1 || {};
                  const t2 = m.team2 || {};

                  const t1id = t1.id === removed.id ? kept.id : t1.id;
                  const t2id = t2.id === removed.id ? kept.id : t2.id;
                  if (t1id !== t1.id || t2id !== t2.id) anyChanged = true;

                  return {
                    ...m,
                    team1: { ...t1, id: t1id },
                    team2: { ...t2, id: t2id },
                  };
                });
                return { ...wk, matches: newMatches };
              });
              if (anyChanged) {
                await updateDoc(
                  doc(db, "Pennants", pDoc.id, "draws", dDoc.id),
                  { weeks: newWeeks }
                );
              }
            })
          );

          // 5️⃣ Sweep each "teams" sub-collection
          const teamsCol = collection(db, "Pennants", pDoc.id, "teams");
          const teamSnap = await getDocs(teamsCol);
          await Promise.all(
            teamSnap.docs.map(async (tDoc) => {
              const td = tDoc.data() || {};
              const memObj = td.members || {};
              let teamChanged = false;
              const newMemObj = {};

              Object.entries(memObj).forEach(([idx, mem]) => {
                if (mem.id === removed.id) {
                  newMemObj[idx] = { ...mem, id: kept.id };
                  teamChanged = true;
                }
              });

              if (teamChanged) {
                await updateDoc(
                  doc(db, "Pennants", pDoc.id, "teams", tDoc.id),
                  { members: { ...memObj, ...newMemObj } }
                );
              }
            })
          );
        })
      );

      // 6️⃣ Delete the duplicate from Square
      if (removed.squareCustomerId || removed.id) {
        const delFn = httpsCallable(functions, "deleteSquareCustomer");
        await delFn({
          customerId: removed.squareCustomerId || removed.id,
        }).catch(() => {});
      }

      // 7️⃣ Delete the duplicate Member doc
      await deleteDoc(doc(db, "members", removed.id));

      // 8️⃣ Relink any User docs that pointed at the old ID
      const uQ = query(
        collection(db, "users"),
        where("squareCustomerId", "==", removed.id)
      );
      const userSnap = await getDocs(uQ);
      await Promise.all(
        userSnap.docs.map((uDoc) =>
          updateDoc(doc(db, "users", uDoc.id), {
            squareCustomerId: kept.id,
            memberDocId: kept.id,
          })
        )
      );

      // done!
      afterMerge();
      onClose();
    } catch (err) {
      console.error("MergeMembersDialog error:", err);
      alert("Merge failed—see console for details.");
    }
  };

  // if we’re not fully ready, render nothing
  if (!open || !left || !right) return null;

  return (
    <Dialog open={open} maxWidth="lg" fullWidth onClose={onClose}>
      <DialogTitle>Merge Members</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
        >
          <Typography>
            <strong>Kept:</strong> {kept.firstName} {kept.lastName} (
            <code>{kept.id}</code>)
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              // swap which record is kept vs removed
              setKept(removed);
              setRemoved(kept);
              // reset all radio choices
              setChoice(Object.fromEntries(allKeys.map((k) => [k, "left"])));
            }}
          >
            Swap Sides
          </Button>
        </Box>

        {showWarn && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {keptPennCount > 0 && (
              <Typography>
                • Kept has {keptPennCount} pennant
                {keptPennCount > 1 ? "s" : ""}.
              </Typography>
            )}
            {remPennCount > 0 && (
              <Typography sx={{ mt: 1 }}>
                • Removed has {remPennCount} pennant
                {remPennCount > 1 ? "s" : ""}.
              </Typography>
            )}
            This will also “Update Pennants” references, ensure merge is selcted for pennants field.
          </Alert>
        )}

        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableCell>Field</TableCell>
              <TableCell align="center">{kept.firstName}</TableCell>
              <TableCell align="center">{removed.firstName}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allKeys.map((key) => {
              const lv = kept[key];
              const rv = removed[key];
              const canMerge =
                typeof lv === "object" || Array.isArray(lv);

              return (
                <TableRow key={key} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                  <TableCell>
                    <Tooltip title={JSON.stringify(lv, null, 2)}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Radio
                          size="small"
                          checked={choice[key] === "left"}
                          onChange={() => pick(key, "left")}
                        />
                        <Typography noWrap maxWidth={200}>
                          {preview(lv)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={JSON.stringify(rv, null, 2)}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Radio
                          size="small"
                          checked={choice[key] === "right"}
                          onChange={() => pick(key, "right")}
                        />
                        <Typography noWrap maxWidth={200}>
                          {preview(rv)}
                        </Typography>
                        {canMerge && (
                          <FormControlLabel
                            control={
                              <Radio
                                size="small"
                                checked={choice[key] === "merge"}
                                onChange={() => pick(key, "merge")}
                              />
                            }
                            label="merge"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={showWarn ? "warning" : "primary"}
          onClick={handleMerge}
        >
          {showWarn ? "Merge & Update Pennants" : "Merge & Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
